import CloseIcon from '@mui/icons-material/Close';
import { Stack, Typography } from '@mui/material';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import PaiListItem from 'features/ui/base-components/pai-list-item';
import PaiButton from 'features/ui/base-components/pai-button';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  thirdPartyScreenshot: string | null;
  markerPosition: { x: number; y: number; percentageX: number; percentageY: number } | null;
  getThirdPartyScreenshotLoading: boolean;
  thirdPartyClickLoading: boolean;

  handleImageClick(event: React.MouseEvent): void;

  handleDeleteMarker(event: React.MouseEvent): void;

  handleThirdPartyClick(): void;
}

const ThirdPartyClick: React.FC<Props> = ({
  thirdPartyScreenshot,
  markerPosition,
  getThirdPartyScreenshotLoading,
  thirdPartyClickLoading,
  handleImageClick,
  handleDeleteMarker,
  handleThirdPartyClick,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const loading = getThirdPartyScreenshotLoading || thirdPartyClickLoading;

  return (
    <Stack className={classes.thirdPartyClick} gap={3}>
      <Typography className={classes.bigHighlightText} align={'center'}>
        {t('appSyncThirdParty')}
      </Typography>
      <Stack direction={'row'} gap={4}>
        <Stack gap={2}>
          <Typography className={classes.greyFormText}>{t('appSyncThirdPartyHeadsUp')}</Typography>
          <Stack gap={1}>
            <PaiListItem text={t('appSyncThirdPartyClickInstruction1')} size={'large'} disablePadding={true} />
            <PaiListItem text={t('appSyncThirdPartyClickInstruction2')} size={'large'} disablePadding={true} />
            <PaiListItem text={t('appSyncThirdPartyClickInstruction3')} size={'large'} disablePadding={true} />
            <PaiListItem text={t('appSyncThirdPartyClickInstruction4')} size={'large'} highlight={true} disablePadding={true} />
          </Stack>
        </Stack>
        {!_.isNil(thirdPartyScreenshot) ? (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          (<div
            className={classes.thirdPartyClickContainer}
            role={'button'}
            tabIndex={0}
            onClick={!loading ? handleImageClick : undefined}
            style={{ cursor: !loading ? 'pointer' : undefined }}
          >
            {loading ? (
              <div className={classes.loaderContainer}>
                <PaiDotLoader size={'large'} />
              </div>
            ) : null}
            <img
              className={cx(classes.thirdPartyScreenshot, loading ? 'loading' : undefined)}
              src={thirdPartyScreenshot}
              alt={'third-party-website'}
            />
            {!_.isNil(markerPosition) && !loading ? (
              (<div
                className={classes.marker}
                role={'button'}
                tabIndex={0}
                style={{ left: `calc(${markerPosition.x}px - 24px)`, top: `calc(${markerPosition.y}px - 24px)` }}
                onContextMenu={handleDeleteMarker}
              >
                <CloseIcon className={classes.innerMarker} />
              </div>)
            ) : null}
          </div>)
        ) : null}
      </Stack>
      <Stack direction={'row'} justifyContent={'flex-end'}>
        <PaiButton
          text={t('continue')}
          border={'rounded'}
          action={handleThirdPartyClick}
          disabled={_.isNil(markerPosition)}
          loading={thirdPartyClickLoading}
        />
      </Stack>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  thirdPartyClick: {
    width: '100%',
  },
  text: {
    fontSize: theme.typography.pxToRem(13),
  },
  bigHighlightText: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  greyFormText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  highlightText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  thirdPartyClickContainer: {
    position: 'relative',
    width: 'fit-content',
    margin: '0 auto',
  },
  loaderContainer: {
    position: 'absolute',
    zIndex: 999,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
  marker: {
    position: 'absolute',
    width: '48px',
    height: '48px',
    border: `4px solid ${theme.palette.primary.main}`,
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  innerMarker: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.error.main,
    pointerEvents: 'none',
  },
  thirdPartyScreenshot: {
    height: '340px',
    width: 'auto',
    '&.loading': {
      filter: 'saturate(0%)',
    },
  },
}));

export default ThirdPartyClick;
